import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText, Upload, Loader2, Users, History, AlertTriangle, Check, X, Send
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only Policy Version Manager.
 * Lists all 6 policies with current version + re-sign status counts.
 * "Upload new version" opens a file picker + notes field.
 * Every existing signature at an older version instantly becomes stale,
 * and affected employees see the `PolicyResignBanner` on next login.
 */
const PolicyVersionManager = ({ authAxios }) => {
  const [data, setData] = useState({ policies: [], resign_deadline_days: 7 });
  const [loading, setLoading] = useState(true);
  const [activePolicy, setActivePolicy] = useState(null);
  const [nudging, setNudging] = useState(false);

  const runNudges = async () => {
    if (nudging) return;
    setNudging(true);
    try {
      const { data: s } = await authAxios.post(`${API_URL}/api/admin/policies/run-nudges-now`);
      alert(`HR nudges sent!\n\n• Emails: ${s.nudged_email}\n• WhatsApp: ${s.nudged_whatsapp}\n• Cooldown skipped: ${s.skipped_cooldown}\n• Errors: ${s.errors}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Nudge run failed");
    }
    setNudging(false);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/policies`);
      setData(r.data);
    } catch {}
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totalPending = data.policies.reduce((s, p) => s + (p.resign_pending || 0), 0);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="policy-version-manager">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#DC283C] flex items-center justify-center">
            <FileText size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm">Company Policy Versions</h3>
            <p className="text-xs text-[#94a3b8]">
              Upload new versions of Big Kreation policies · employees get a {data.resign_deadline_days}-day window to re-sign
            </p>
          </div>
        </div>
        {totalPending > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
              <AlertTriangle size={12} /> {totalPending} re-signs pending
            </div>
            <button
              onClick={runNudges}
              disabled={nudging}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#1E1B4B] text-white text-xs font-bold hover:bg-[#2d2a6d] disabled:opacity-50"
              title="Email + WhatsApp every employee with a stale policy (auto-runs every 6h; this button forces a pass now)"
              data-testid="run-nudges-btn"
            >
              {nudging ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {nudging ? "Nudging…" : "Nudge Now"}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
      ) : (
        <div className="space-y-2">
          {data.policies.map((p) => (
            <div
              key={p.key}
              className="flex items-center gap-3 border border-gray-100 rounded-xl p-3 hover:border-[#E83E8C]/30"
              data-testid={`policy-version-row-${p.key}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[#1E1B4B]">{p.title}</p>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">{p.key}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">v{p.current_version}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1"><Check size={10} className="text-green-500" /> {p.signed_current} signed current</span>
                  {p.resign_pending > 0 && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle size={10} /> {p.resign_pending} need re-sign</span>}
                  <span className="flex items-center gap-1"><Users size={10} /> {p.never_signed} never signed</span>
                </div>
                {p.notes && <p className="text-[10px] text-gray-400 mt-0.5 truncate italic">"{p.notes}"</p>}
              </div>
              <button
                onClick={() => setActivePolicy(p)}
                className="px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-bold hover:bg-[#c22d78] flex items-center gap-1"
                data-testid={`upload-new-version-${p.key}`}
              >
                <Upload size={12} /> New Version
              </button>
            </div>
          ))}
        </div>
      )}

      {activePolicy && (
        <UploadVersionModal
          policy={activePolicy}
          authAxios={authAxios}
          onClose={() => setActivePolicy(null)}
          onUploaded={() => { setActivePolicy(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

const UploadVersionModal = ({ policy, authAxios, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) { setError("Please choose a PDF"); return; }
    setSubmitting(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("notes", notes);
      const { data } = await authAxios.post(`${API_URL}/api/admin/policies/${policy.key}/new-version`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert(`Uploaded v${data.version}. ${data.affected_employees} employee(s) will be asked to re-sign within ${data.resign_deadline_days} days.`);
      onUploaded();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed — please retry.");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="upload-version-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="px-5 py-3 bg-gradient-to-r from-[#1E1B4B] to-[#E83E8C] text-white flex items-start justify-between">
          <div>
            <h3 className="font-bold text-sm">Upload New Version</h3>
            <p className="text-[10px] text-white/70">{policy.title} · currently v{policy.current_version}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="upload-version-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
            data-testid="upload-version-file-input"
          />
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-[#E83E8C] flex flex-col items-center gap-2 text-gray-500 hover:text-[#E83E8C]"
          >
            <Upload size={22} />
            <span className="text-sm font-semibold">{file ? file.name : "Click to choose PDF"}</span>
            {file && <span className="text-[10px]">{(file.size / 1024).toFixed(1)} KB</span>}
          </button>
          <div>
            <label className="text-xs font-semibold text-[#1E1B4B]">Release notes (optional — employees see this)</label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Added 14-day notice clause for sabbaticals"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E83E8C]"
              data-testid="upload-version-notes"
            />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <History size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700">
              Uploading v{policy.current_version + 1} makes all existing signatures stale. Employees will see a re-sign banner until they sign the new version.
            </p>
          </div>
          {error && <p className="text-xs text-red-500" data-testid="upload-version-error">{error}</p>}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-xs rounded-xl border border-gray-200">Cancel</button>
            <button
              onClick={submit}
              disabled={!file || submitting}
              className="px-5 py-2 text-xs rounded-xl bg-[#E83E8C] text-white font-bold hover:bg-[#c22d78] disabled:opacity-40 flex items-center gap-1.5"
              data-testid="upload-version-submit"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {submitting ? "Uploading…" : "Publish New Version"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyVersionManager;
